import { LabelSelector, type Firewall, type FirewallServer } from "@cloudserver/contracts";
import { useReadOnly } from "../api/users.js";
import { Link, useBlocker, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Plus, Shield, Tags, Trash2, Unplug } from "lucide-react";
import { useState, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  useApplyFirewall,
  useDeleteFirewall,
  useFirewall,
  useRemoveFirewall,
  useSetFirewallRules,
  useUpdateFirewall,
} from "../api/firewalls.js";
import { progressOf, useRunningOperations } from "../api/networks.js";
import { ApiProblem } from "../api/client.js";
import { useServers } from "../api/servers.js";
import { DangerDialog } from "../ui/confirm.js";
import { ErrorMessage, fieldError } from "../ui/feedback.js";
import { EmptyState, PageHeader, Section, Tabs } from "../ui/layout.js";
import { Dialog } from "../ui/overlay.js";
import { Alert, Badge, Button, Card, Spinner, TextField } from "../ui/primitives.js";
import { DataTable } from "../ui/table.js";
import { useToast } from "../ui/toast.js";
import { ServerChecklist } from "./firewall-new.js";
import {
  draftsOf,
  draftsSignature,
  hasErrors,
  RuleEditor,
  rulesOf,
  type RuleDraft,
} from "./firewall-rules.js";
import { isApplied } from "./firewalls.js";
import { formatLabels } from "./labels.js";
import { NameAndLabels } from "./network.js";
import { failureToast } from "./networks.js";

export const FIREWALL_TABS = ["rules", "resources", "settings", "delete"] as const;
export type FirewallTab = (typeof FIREWALL_TABS)[number];

const TAB_INTRO: Record<FirewallTab, string> = {
  rules: "firewalls.rulesIntro",
  resources: "firewalls.resourcesIntro",
  settings: "firewalls.settingsIntro",
  delete: "firewalls.deleteIntro",
};

interface TabProps {
  projectId: string;
  firewall: Firewall;
  readOnly: boolean;
  /** Why changes are refused (guest or read-only token); empty when allowed. */
  readOnlyReason: string;
}

function RulesTab({ projectId, firewall, readOnly }: TabProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const save = useSetFirewallRules(projectId, firewall.id);
  const [drafts, setDrafts] = useState<RuleDraft[]>(() => draftsOf(firewall.rules));
  const [tried, setTried] = useState(false);
  // What was last sent: the editor is not "unsaved" while that change is running.
  const [sent, setSent] = useState<string | null>(null);
  const current = draftsSignature(drafts);
  const dirty = current !== draftsSignature(draftsOf(firewall.rules)) && current !== sent;
  const blocker = useBlocker({
    shouldBlockFn: () => dirty && !save.isPending,
    enableBeforeUnload: () => dirty,
    withResolver: true,
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTried(true);
    if (hasErrors(drafts)) return;
    save.mutate(rulesOf(drafts), {
      onSuccess: () => {
        toast({ tone: "ok", title: t("firewalls.saving") });
        setSent(current);
        setTried(false);
      },
    });
  };
  const discard = () => {
    setDrafts(draftsOf(firewall.rules));
    setTried(false);
    save.reset();
  };

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-6">
      {dirty && (
        <div className="sticky top-14 z-10 flex flex-wrap items-center gap-3 rounded-xl border border-warn bg-warn-soft px-4 py-2 text-sm">
          <span className="font-medium text-warn">{t("firewalls.unsaved")}</span>
          <div className="ml-auto flex flex-wrap gap-2">
            <Button variant="secondary" onClick={discard}>
              {t("firewalls.discard")}
            </Button>
            <Button type="submit" busy={save.isPending} disabled={readOnly}>
              {t("firewalls.save")}
            </Button>
          </div>
        </div>
      )}
      {tried && hasErrors(drafts) && <Alert tone="danger">{t("firewalls.fixErrors")}</Alert>}
      <ErrorMessage error={save.error} />
      {save.error instanceof ApiProblem && save.error.problem?.fields?.length ? (
        <Alert tone="danger">{t("firewalls.fixErrors")}</Alert>
      ) : null}
      <Section title={t("firewalls.stepInbound")}>
        <RuleEditor
          direction="in"
          drafts={drafts}
          onChange={setDrafts}
          readOnly={readOnly}
          showErrors={tried}
          askAbout={firewall}
        />
      </Section>
      <Section title={t("firewalls.stepOutbound")} description={t("firewalls.stepOutboundLead")}>
        <RuleEditor
          direction="out"
          drafts={drafts}
          onChange={setDrafts}
          readOnly={readOnly}
          showErrors={tried}
          askAbout={firewall}
        />
      </Section>
      <Dialog
        open={blocker.status === "blocked"}
        onOpenChange={(open) => !open && blocker.reset?.()}
        title={t("firewalls.leaveTitle")}
        description={t("firewalls.leaveLead")}
        footer={
          <>
            <Button variant="secondary" onClick={() => blocker.reset?.()}>
              {t("firewalls.stay")}
            </Button>
            <Button variant="danger-solid" onClick={() => blocker.proceed?.()}>
              {t("firewalls.leave")}
            </Button>
          </>
        }
      />
    </form>
  );
}

type Target =
  | { type: "server"; key: string; server: FirewallServer }
  | { type: "selector"; key: string; selector: string; servers: FirewallServer[] };

function ApplyServersDialog({
  projectId,
  firewall,
  onClose,
}: {
  projectId: string;
  firewall: Firewall;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const servers = useServers(projectId);
  const apply = useApplyFirewall(projectId, firewall.id);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const direct = new Set(firewall.appliedTo.servers.map((s) => s.id));
  const candidates = (servers.data ?? []).filter((s) => !direct.has(s.id));
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("firewalls.applyServers")}
      description={t("firewalls.applyServersLead")}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            busy={apply.isPending}
            disabled={chosen.size === 0}
            onClick={() =>
              apply.mutate(
                { serverIds: [...chosen] },
                {
                  onSuccess: () => {
                    toast({ tone: "ok", title: t("firewalls.applying") });
                    onClose();
                  },
                },
              )
            }
          >
            {t("firewalls.apply")}
          </Button>
        </>
      }
    >
      {servers.isPending ? (
        <Spinner label={t("common.loading")} />
      ) : candidates.length === 0 ? (
        <Alert tone="neutral">{t("firewalls.noCandidates")}</Alert>
      ) : (
        <div className="flex flex-col gap-3">
          <ServerChecklist servers={candidates} chosen={chosen} onChange={setChosen} />
          <ErrorMessage error={apply.error} />
        </div>
      )}
    </Dialog>
  );
}

function ApplySelectorDialog({
  projectId,
  firewall,
  onClose,
}: {
  projectId: string;
  firewall: Firewall;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const apply = useApplyFirewall(projectId, firewall.id);
  const [selector, setSelector] = useState("");
  const [tried, setTried] = useState(false);
  const valid = LabelSelector.safeParse(selector).success;
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTried(true);
    if (!valid) return;
    apply.mutate(
      { labelSelectors: [selector.trim()] },
      {
        onSuccess: () => {
          toast({ tone: "ok", title: t("firewalls.applying") });
          onClose();
        },
      },
    );
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("firewalls.applySelector")}
      description={t("firewalls.applySelectorLead")}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="apply-selector" busy={apply.isPending}>
            {t("firewalls.apply")}
          </Button>
        </>
      }
    >
      <form id="apply-selector" onSubmit={submit} noValidate className="flex flex-col gap-4">
        <TextField
          label={t("firewalls.selector")}
          name="selector"
          value={selector}
          onChange={(e) => setSelector(e.target.value)}
          spellCheck={false}
          placeholder="role=web"
          hint={t("firewalls.selectorHint")}
          error={
            tried && !valid
              ? t("firewalls.selectorInvalid")
              : fieldError(apply.error, "labelSelectors", t)
          }
        />
        <ErrorMessage error={apply.error} />
      </form>
    </Dialog>
  );
}

function ResourcesTab({ projectId, firewall, readOnly, readOnlyReason }: TabProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const remove = useRemoveFirewall(projectId, firewall.id);
  const [dialog, setDialog] = useState<"servers" | "selector" | null>(null);
  const [removing, setRemoving] = useState<Target | null>(null);
  const targets: Target[] = [
    ...firewall.appliedTo.servers.map((server) => ({
      type: "server" as const,
      key: `server:${server.id}`,
      server,
    })),
    ...firewall.appliedTo.labelSelectors.map((s) => ({
      type: "selector" as const,
      key: `selector:${s.selector}`,
      selector: s.selector,
      servers: s.servers,
    })),
  ];
  const nameOf = (target: Target) =>
    target.type === "server" ? target.server.name : target.selector;
  const buttons = (
    <div className="flex flex-wrap gap-2">
      <Button
        onClick={() => setDialog("servers")}
        disabled={readOnly}
        icon={<Plus className="size-4" aria-hidden />}
      >
        {t("firewalls.applyServers")}
      </Button>
      <Button
        variant="secondary"
        onClick={() => setDialog("selector")}
        disabled={readOnly}
        icon={<Tags className="size-4" aria-hidden />}
      >
        {t("firewalls.applySelector")}
      </Button>
    </div>
  );
  return (
    <>
      <Section title={t("firewalls.target")} actions={targets.length ? buttons : null}>
        <DataTable<Target>
          label={t("firewalls.target")}
          rows={targets}
          rowKey={(row) => row.key}
          empty={
            <EmptyState
              icon={<Shield className="size-6" aria-hidden />}
              title={t("firewalls.notApplied")}
              action={buttons}
            >
              {t("firewalls.notAppliedLead")}
            </EmptyState>
          }
          columns={[
            {
              key: "name",
              header: t("firewalls.target"),
              primary: true,
              cell: (row) => (
                <span className={row.type === "selector" ? "font-mono text-xs" : "font-medium"}>
                  {nameOf(row)}
                </span>
              ),
            },
            {
              key: "type",
              header: t("firewalls.targetKind"),
              cell: (row) =>
                row.type === "server" ? (
                  <Badge>{t("firewalls.targetServer")}</Badge>
                ) : (
                  <Badge>{t("firewalls.targetSelector")}</Badge>
                ),
            },
            {
              key: "matches",
              header: t("firewalls.matches"),
              cell: (row) =>
                row.type === "server" ? (
                  "—"
                ) : row.servers.length ? (
                  row.servers.map((s) => s.name).join(", ")
                ) : (
                  <span className="text-muted">{t("firewalls.matchesNone")}</span>
                ),
            },
          ]}
          rowMenu={(row) => [
            [
              {
                label: t("firewalls.remove"),
                icon: <Unplug className="size-4" aria-hidden />,
                danger: true,
                disabled: readOnly,
                reason: readOnlyReason,
                onSelect: () => setRemoving(row),
              },
            ],
          ]}
        />
      </Section>
      {dialog === "servers" && (
        <ApplyServersDialog
          projectId={projectId}
          firewall={firewall}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === "selector" && (
        <ApplySelectorDialog
          projectId={projectId}
          firewall={firewall}
          onClose={() => setDialog(null)}
        />
      )}
      <DangerDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        title={t("firewalls.removeTitle", { name: removing ? nameOf(removing) : "" })}
        consequences={[
          removing?.type === "selector"
            ? t("firewalls.removeSelectorConsequence")
            : t("firewalls.removeConsequence"),
        ]}
        actionLabel={t("firewalls.remove")}
        busy={remove.isPending}
        onConfirm={() =>
          removing &&
          remove.mutate(
            removing.type === "server"
              ? { serverIds: [removing.server.id] }
              : { labelSelectors: [removing.selector] },
            {
              onSuccess: () => {
                toast({ tone: "ok", title: t("firewalls.removing") });
                setRemoving(null);
              },
              onError: (error) => toast(failureToast(t, error)),
            },
          )
        }
      />
    </>
  );
}

function SettingsTab({ projectId, firewall, readOnly }: TabProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const update = useUpdateFirewall(projectId, firewall.id);
  return (
    <div className="flex flex-col gap-4">
      <NameAndLabels
        name={firewall.name}
        labels={firewall.labels}
        readOnly={readOnly}
        saving={update.isPending}
        error={update.error}
        onSave={(change) =>
          update.mutate(change, {
            onSuccess: () => toast({ tone: "ok", title: t("firewalls.saved") }),
          })
        }
      />
    </div>
  );
}

function DeleteTab({ projectId, firewall, readOnly }: TabProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const remove = useDeleteFirewall(projectId);
  const detachAll = useRemoveFirewall(projectId, firewall.id);
  const [confirming, setConfirming] = useState<"delete" | "remove-all" | null>(null);
  const applied = isApplied(firewall);
  return (
    <Card>
      <div className="flex flex-col gap-4">
        {applied ? (
          <Alert tone="warn">{t("firewalls.deleteBlocked")}</Alert>
        ) : (
          <ul className="flex list-disc flex-col gap-1 pl-5 text-sm">
            <li>{t("firewalls.deleteRules")}</li>
            <li>{t("firewalls.deleteNoEffect")}</li>
          </ul>
        )}
        <div className="flex flex-wrap gap-2">
          {applied && (
            <Button
              variant="secondary"
              disabled={readOnly}
              icon={<Unplug className="size-4" aria-hidden />}
              onClick={() => setConfirming("remove-all")}
            >
              {t("firewalls.removeAll")}
            </Button>
          )}
          <Button
            variant="danger"
            disabled={readOnly || applied}
            icon={<Trash2 className="size-4" aria-hidden />}
            onClick={() => setConfirming("delete")}
          >
            {t("firewalls.delete")}
          </Button>
        </div>
      </div>
      <DangerDialog
        open={confirming === "remove-all"}
        onOpenChange={(open) => !open && setConfirming(null)}
        title={t("firewalls.removeAllTitle", { name: firewall.name })}
        consequences={[t("firewalls.removeAllConsequence")]}
        actionLabel={t("firewalls.removeAll")}
        busy={detachAll.isPending}
        onConfirm={() =>
          detachAll.mutate(
            {
              serverIds: firewall.appliedTo.servers.map((s) => s.id),
              labelSelectors: firewall.appliedTo.labelSelectors.map((s) => s.selector),
            },
            {
              onSuccess: () => {
                toast({ tone: "ok", title: t("firewalls.removing") });
                setConfirming(null);
              },
              onError: (error) => toast(failureToast(t, error)),
            },
          )
        }
      />
      <DangerDialog
        open={confirming === "delete"}
        onOpenChange={(open) => !open && setConfirming(null)}
        title={t("firewalls.deleteTitle", { name: firewall.name })}
        consequences={[t("firewalls.deleteRules"), t("firewalls.deleteNoEffect")]}
        confirmText={firewall.name}
        actionLabel={t("firewalls.delete")}
        busy={remove.isPending}
        onConfirm={() =>
          // The page unmounts as soon as the copy no longer has the firewall, so
          // the follow-up must not depend on this component's mutate callbacks.
          void remove.mutateAsync(firewall.id).then(
            () => {
              toast({ tone: "ok", title: t("firewalls.deleted", { name: firewall.name }) });
              void navigate({ to: "/projects/$projectId/firewalls", params: { projectId } });
            },
            (error: unknown) => toast(failureToast(t, error)),
          )
        }
      />
    </Card>
  );
}

export function FirewallPage({
  projectId,
  firewallId,
  tab,
}: {
  projectId: string;
  firewallId: string;
  tab: FirewallTab;
}) {
  const { t } = useTranslation();
  const firewall = useFirewall(projectId, firewallId);
  const running = useRunningOperations(projectId);
  const { readOnly, reason: readOnlyReason, token: tokenReadOnly } = useReadOnly(projectId);
  const back = (
    <Link
      to="/projects/$projectId/firewalls"
      params={{ projectId }}
      className="inline-flex min-h-6 items-center gap-1 text-sm text-muted hover:text-text"
    >
      <ArrowLeft className="size-4" aria-hidden />
      {t("firewalls.back")}
    </Link>
  );
  if (firewall.error) {
    const gone = firewall.error instanceof ApiProblem && firewall.error.status === 404;
    return (
      <>
        <PageHeader title={t("nav.firewalls")} intro={gone ? t("firewalls.notFound") : undefined} />
        {back}
        {!gone && <ErrorMessage error={firewall.error} />}
      </>
    );
  }
  if (!firewall.data) return <Spinner label={t("common.loading")} />;
  const f = firewall.data;
  const progress = progressOf(running.data, f.id);
  const props = { projectId, firewall: f, readOnly, readOnlyReason };
  const views: Record<FirewallTab, ReactNode> = {
    // Remounted when the saved rules change, so the editor starts from them.
    rules: <RulesTab key={draftsSignature(draftsOf(f.rules))} {...props} />,
    resources: <ResourcesTab {...props} />,
    settings: <SettingsTab key={`${f.name}|${formatLabels(f.labels)}`} {...props} />,
    delete: <DeleteTab {...props} />,
  };
  return (
    <>
      {back}
      <PageHeader
        title={f.name}
        help="firewalls"
        meta={
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge>
              {t("firewalls.rulesCount", {
                inbound: f.rules.filter((r) => r.direction === "in").length,
                outbound: f.rules.filter((r) => r.direction === "out").length,
              })}
            </Badge>
            <Badge tone={isApplied(f) ? "ok" : "neutral"}>
              {isApplied(f)
                ? t("firewalls.appliedCount", {
                    servers: f.appliedTo.servers.length,
                    selectors: f.appliedTo.labelSelectors.length,
                  })
                : t("firewalls.notApplied")}
            </Badge>
          </div>
        }
        intro={t(TAB_INTRO[tab])}
      />
      {tokenReadOnly && <Alert tone="warn">{readOnlyReason}</Alert>}
      {progress !== null && (
        <Alert tone="neutral">
          {t("firewalls.running")} {progress}%
        </Alert>
      )}
      <Tabs
        label={f.name}
        active={tab}
        items={FIREWALL_TABS.map((key) => ({
          key,
          label: t(`firewalls.tabs.${key}`),
          link: {
            to: "/projects/$projectId/firewalls/$firewallId",
            params: { projectId, firewallId },
            search: { tab: key },
          },
        }))}
      />
      {views[tab]}
    </>
  );
}
